// @ts-nocheck

import merge from 'lodash/merge'

export interface ToastrOptions {
  closeButton?: boolean
  newestOnTop?: boolean
  progressBar?: boolean
  positionClass?: string
  preventDuplicates?: boolean
  onclick?: any | null
  showDuration?: number
  hideDuration?: number
  showEasing?: string
  hideEasing?: string
  showMethod?: string
  hideMethod?: string
  tapToDismiss?: boolean
  containerId?: string
  iconClasses?: {
    error: string
    info: string
    success: string
    warning: string
  }
  iconClass?: string
  target?: string
  closeHtml?: string
  titleClass?: string
  messageClass?: string
  toastClass?: string
  escapeHtml?: boolean
  closeClass?: string
  newestOnBottom?: boolean
  allowHtml?: boolean
  closeMethod?: string | boolean
  progressClass?: string
  closeDuration?: string | boolean
  closeEasing?: string | boolean
  closeOnHover?: boolean
  extendedTimeOut?: number
  timeOut?: number
  onHidden?: any
  endTime?: Date
  onCloseClick?: any
  onShown?: any
}

export class Toastr {
  $container
  listener
  previousToast
  toastId = 0

  toastType = {
    error: 'error',
    info: 'info',
    success: 'success',
    warning: 'warning',
  }

  message: string
  title: string

  options: ToastrOptions

  defaultOptions: ToastrOptions = {
    tapToDismiss: true,
    toastClass: 'toast',
    containerId: 'toast-container',
    showMethod: 'fadeIn', // fadeIn, slideDown, and show are built into jQuery
    showDuration: 300,
    showEasing: 'swing', // swing and linear are built into jQuery
    onShown: undefined,
    hideMethod: 'fadeOut',
    hideDuration: 1000,
    hideEasing: 'swing',
    onHidden: undefined,
    closeMethod: false,
    closeDuration: false,
    closeEasing: false,
    closeOnHover: true,
    extendedTimeOut: 1000,
    iconClasses: {
      error: 'toast-error',
      info: 'toast-info',
      success: 'toast-success',
      warning: 'toast-warning',
    },
    iconClass: 'toast-info',
    positionClass: 'toast-top-right',
    timeOut: 5000, // Set timeOut and extendedTimeOut to 0 to make it sticky
    titleClass: 'toast-title',
    messageClass: 'toast-message',
    escapeHtml: false,
    target: 'body',
    closeHtml: '<button type="button">&times;</button>',
    closeClass: 'toast-close-button',
    newestOnTop: true,
    preventDuplicates: false,
    progressBar: false,
    progressClass: 'toast-progress',
  }

  constructor(message: string = undefined, title: string = undefined, options: ToastrOptions = {}) {
    this.message = message
    this.title = title
    this.options = merge({}, this.defaultOptions, options)
  }

  setOptions(options: ToastrOptions) {
    this.options = merge({}, this.options, options)
  }

  error(message, title, optionsOverride = {}) {
    return this.notify({
      type: this.toastType.error,
      iconClass: this.options.iconClasses.error,
      message: message,
      optionsOverride: optionsOverride,
      title: title,
    })
  }

  success(message, title, optionsOverride = {}) {
    return this.notify({
      type: this.toastType.success,
      iconClass: this.options.iconClasses.success,
      message: message,
      optionsOverride: optionsOverride,
      title: title,
    })
  }

  info(message, title, optionsOverride = {}) {
    return this.notify({
      type: this.toastType.info,
      iconClass: this.options.iconClasses.info,
      message: message,
      optionsOverride: optionsOverride,
      title: title,
    })
  }

  warning(message, title, optionsOverride = {}) {
    return this.notify({
      type: this.toastType.warning,
      iconClass: this.options.iconClasses.warning,
      message: message,
      optionsOverride: optionsOverride,
      title: title,
    })
  }

  removeToast = ($toastElement) => {
    if (!this.$container) this.$container = this.getContainer()

    if ($toastElement.is(':visible')) return

    $toastElement.remove()
    $toastElement = null

    if (this.$container.children().length === 0) {
      this.$container.remove()
      this.previousToast = undefined
    }
  }

  notify({ type, iconClass, message, optionsOverride, title }) {
    iconClass = iconClass || this.options.iconClass

    if (optionsOverride) {
      this.options = merge({}, this.options, optionsOverride)
      iconClass = optionsOverride.iconClass || iconClass
    }

    const shouldExit = (message) => {
      if (this.options.preventDuplicates) {
        if (message === this.previousToast) {
          return true
        }

        this.previousToast = message
      }

      return false
    }

    if (shouldExit(message)) return

    this.toastId++

    this.$container = this.getContainer(true)

    let intervalId = null
    const $toastElement = $('<div/>')
    const $titleElement = $('<div/>')
    const $messageElement = $('<div/>')
    const $progressElement = $('<div/>')
    const $closeElement = $(this.options.closeHtml)

    const progressBar = {
      intervalId: null,
      hideEta: null,
      maxHideTime: null,
    }

    const response = {
      toastId: this.toastId,
      state: 'visible',
      startTime: new Date(),
      options: this.options,
      map: { type, iconClass, message, optionsOverride, title },
    }

    function escapeHtml(source) {
      if (source == null) {
        source = ''
      }

      return source
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
    }

    function personalizeToast() {
      setIcon()
      setTitle()
      setMessage()
      setCloseButton()
      setProgressBar()
      setSequence()
      setAria()
    }

    function setAria() {
      let ariaValue: string

      switch (iconClass) {
        case 'toast-success':
        case 'toast-info':
          ariaValue = 'polite'
          break
        default:
          ariaValue = 'assertive'
      }

      $toastElement.attr('aria-live', ariaValue)
    }

    const handleEvents = () => {
      if (this.options.closeOnHover) {
        $toastElement.hover(stickAround, delayedHideToast)
      }

      if (!this.options.onclick && this.options.tapToDismiss) {
        $toastElement.click(hideToast)
      }

      if (this.options.closeButton && $closeElement) {
        $closeElement.click((event: Event) => {
          event.stopPropagation()

          if (this.options.onCloseClick) {
            this.options.onCloseClick(event)
          }

          hideToast(true)
        })
      }

      if (this.options.onclick) {
        $toastElement.click((event) => {
          this.options.onclick(event)

          hideToast()
        })
      }
    }

    const displayToast = () => {
      $toastElement.hide()

      $toastElement[this.options.showMethod]({
        duration: this.options.showDuration,
        easing: this.options.showEasing,
        complete: this.options.onShown,
      })

      if (this.options.timeOut > 0) {
        intervalId = setTimeout(hideToast, this.options.timeOut)
        progressBar.maxHideTime = parseFloat(this.options.timeOut?.toString())
        progressBar.hideEta = new Date().getTime() + progressBar.maxHideTime

        if (this.options.progressBar) {
          progressBar.intervalId = setInterval(updateProgress, 10)
        }
      }
    }

    const setIcon = () => {
      if (iconClass) {
        $toastElement.addClass(this.options.toastClass).addClass(iconClass)
      }
    }

    const setSequence = () => {
      if (this.options.newestOnTop) {
        this.$container.prepend($toastElement)
      } else {
        this.$container.append($toastElement)
      }
    }

    const setTitle = () => {
      if (title) {
        let suffix = title

        if (this.options.escapeHtml) {
          suffix = escapeHtml(title)
        }

        $titleElement.append(suffix).addClass(this.options.titleClass)
        $toastElement.append($titleElement)
      }
    }

    const setMessage = () => {
      if (!message) return

      let suffix = message

      if (this.options.escapeHtml) {
        suffix = escapeHtml(message)
      }

      $messageElement.append(suffix).addClass(this.options.messageClass)
      $toastElement.append($messageElement)
    }

    const setCloseButton = () => {
      if (this.options.closeButton) {
        $closeElement.addClass(this.options.closeClass).attr('role', 'button')
        $toastElement.prepend($closeElement)
      }
    }

    const setProgressBar = () => {
      if (this.options.progressBar) {
        $progressElement.addClass(this.options.progressClass)
        $toastElement.prepend($progressElement)
      }
    }

    const hideToast = (override = false) => {
      const method = override && this.options.closeMethod !== false ? this.options.closeMethod : this.options.hideMethod
      const duration = override && this.options.closeDuration !== false ? this.options.closeDuration : this.options.hideDuration
      const easing = override && this.options.closeEasing !== false ? this.options.closeEasing : this.options.hideEasing

      if ($(':focus', $toastElement).length && !override) {
        return
      }

      clearTimeout(progressBar.intervalId)

      return $toastElement[method]({
        duration: duration,
        easing: easing,
        complete: () => {
          this.removeToast($toastElement)
          clearTimeout(intervalId)

          if (this.options.onHidden && response.state !== 'hidden') {
            this.options.onHidden()
          }

          response.state = 'hidden'
          response.endTime = new Date()

          this.publish(response)
        },
      })
    }

    const delayedHideToast = () => {
      if (this.options.timeOut > 0 || this.options.extendedTimeOut > 0) {
        intervalId = setTimeout(hideToast, this.options.extendedTimeOut)
        progressBar.maxHideTime = parseFloat(this.options.extendedTimeOut?.toString())
        progressBar.hideEta = new Date().getTime() + progressBar.maxHideTime
      }
    }

    const stickAround = () => {
      clearTimeout(intervalId)

      progressBar.hideEta = 0

      $toastElement.stop(true, true)[this.options.showMethod]({
        duration: this.options.showDuration,
        easing: this.options.showEasing,
      })
    }

    const updateProgress = () => {
      const percentage = ((progressBar.hideEta - (new Date().getTime())) / progressBar.maxHideTime) * 100

      $progressElement.width(percentage + '%')
    }

    personalizeToast()

    displayToast()

    handleEvents()

    this.publish(response)
  }

  publish(args) {
    if (!this.listener) return

    this.listener(args)
  }

  getContainer(create = false) {
    this.$container = $('#' + this.options.containerId)

    if (this.$container.length) {
      return this.$container
    }

    if (create) {
      this.$container = this.createContainer()
    }

    return this.$container
  }

  createContainer() {
    this.$container = $('<div/>')
      .attr('id', this.options.containerId)
      .addClass(this.options.positionClass)

    const el: Element = $(this.options.target)[0]

    this.$container.appendTo(el)

    return this.$container
  }
}

export default Toastr
